import { useMemo, useState } from 'react';

import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { BILLABLE_USAGE_EVENT_LABELS } from '../../../../../backend/src/billing/billing-types';
import { LedgerUsageTableRow } from '@/api/queries/useGetLedgerBillingOverview';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/new/components/shadcn/table';
import { SortIcon } from '@/new/components/table/sort-icon';
import { cn } from '@/new/lib/utils';

type Grouping = 'user' | 'matter' | 'feature';
type UsageTableRow = {
  id: string;
  label: string;
  creditsByWeek: Map<string, number>;
  totalCredits: number;
};

const numberFormatter = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 });
const weekFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

export function UsageHistoryTable({
  rows,
  isPersonalUsage = false,
}: {
  rows: LedgerUsageTableRow[];
  isPersonalUsage?: boolean;
}) {
  const [grouping, setGrouping] = useState<Grouping>(isPersonalUsage ? 'matter' : 'user');
  const [sorting, setSorting] = useState<SortingState>([{ id: 'total', desc: true }]);

  const weeks = useMemo(() => [...new Set(rows.map((row) => row.week))].sort(), [rows]);
  const tableRows = useMemo(() => {
    const grouped = new Map<string, { label: string; creditsByWeek: Map<string, number> }>();
    for (const row of rows) {
      const key = grouping === 'user' ? row.userId : grouping === 'matter' ? row.matterId : row.eventType;
      const label =
        grouping === 'user'
          ? row.userName
          : grouping === 'matter'
            ? row.matterName
            : BILLABLE_USAGE_EVENT_LABELS[row.eventType];
      const item = grouped.get(key) ?? { label, creditsByWeek: new Map<string, number>() };
      item.creditsByWeek.set(row.week, (item.creditsByWeek.get(row.week) ?? 0) + row.credits);
      grouped.set(key, item);
    }
    return [...grouped.entries()]
      .map(([id, item]) => ({
        id,
        ...item,
        totalCredits: [...item.creditsByWeek.values()].reduce((total, credits) => total + credits, 0),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [grouping, rows]);

  const columns = useMemo<ColumnDef<UsageTableRow>[]>(
    () => [
      {
        id: 'label',
        accessorKey: 'label',
        header: grouping,
        enableSorting: false,
        cell: ({ getValue }) => {
          const label = getValue<string>();
          return (
            <span className="block truncate" title={label}>
              {label}
            </span>
          );
        },
      },
      {
        id: 'total',
        accessorKey: 'totalCredits',
        header: 'Total',
        cell: ({ getValue }) => numberFormatter.format(getValue<number>()),
        sortDescFirst: true,
        meta: { sortIcon: 'number' },
      },
      ...weeks.map<ColumnDef<UsageTableRow>>((week) => ({
        id: week,
        accessorFn: (row) => row.creditsByWeek.get(week) ?? 0,
        header: `w/c ${weekFormatter.format(new Date(`${week}T00:00:00Z`))}`,
        cell: ({ row, getValue }) =>
          row.original.creditsByWeek.has(week) ? numberFormatter.format(getValue<number>()) : '—',
        sortDescFirst: true,
        meta: { sortIcon: 'number' },
      })),
    ],
    [grouping, weeks],
  );
  const table = useReactTable({
    data: tableRows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <section>
      <div className="mx-4 mr-2 mb-6 flex flex-wrap items-end gap-4">
        <div className="mr-auto">
          <h2 className="font-medium text-gray-900">Usage table</h2>
          <p className="mt-0.5 text-sm text-gray-500">Weekly credits, grouped and filtered by usage details.</p>
        </div>

        <div className="flex items-center gap-3">
          <label htmlFor="usage-table-grouping" className="text-sm font-medium text-gray-700">
            Group by
          </label>

          <Select value={grouping} onValueChange={(value) => setGrouping(value as Grouping)}>
            <SelectTrigger id="usage-table-grouping" className="h-8 w-auto border-0 bg-transparent px-1.5 py-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              {!isPersonalUsage && <SelectItem value="user">User</SelectItem>}
              <SelectItem value="matter">Matter</SelectItem>
              <SelectItem value="feature">Feature</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {tableRows.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-500">No usage matches these filters.</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader className="bg-gray-50">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={cn(
                        'h-auto px-3 py-2',
                        header.column.id === 'label'
                          ? 'sticky left-0 z-20 w-60 max-w-60 min-w-60 bg-gray-50 text-left capitalize shadow-[inset_-1px_0_0_0_#e5e7eb]'
                          : 'min-w-28 border-r text-right last:border-r-0',
                        header.column.id === 'total' && 'font-medium text-gray-700',
                      )}
                    >
                      <div
                        className={cn(
                          'flex items-center gap-2 select-none',
                          header.column.id !== 'label' && 'cursor-pointer justify-end',
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <SortIcon column={header.column} />
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        'px-3 py-2',
                        cell.column.id === 'label'
                          ? 'sticky left-0 z-10 w-60 max-w-60 min-w-60 bg-white font-medium text-gray-700 shadow-[inset_-1px_0_0_0_#e5e7eb]'
                          : 'border-r text-right text-gray-600 tabular-nums last:border-r-0',
                        cell.column.id === 'total' && 'font-medium text-gray-700',
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
